import { useEffect, useRef } from 'react'
import Highlighter from 'react-highlight-words'
import { RxArrowRight } from 'react-icons/rx'
import Typography from '~/@/components/ui/typography'
import { cn } from '~/@/lib/utils'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { DossIcon } from '~/components/DossIcon'
import { WorkspaceIcon } from '~/components/navigation/WorkspaceIcon'
import { StandardTable } from '~/components/tables/types'
import { IconButton } from '~/components/ui/atoms/icon-button/IconButton'
import { LookupTableAndFieldDropdownEntity } from './LookupTableAndFieldDropdown'

interface LookupTableOptionsProps {
  tableOptions: StandardTable[]
  highlightedOption: StandardTable | null
  setSearchQuery: (searchQuery: string) => void
  searchQuery: string
  setDropdownEntity: (entity: LookupTableAndFieldDropdownEntity) => void
  selectedTableId: string | null
  onTableSelect: (tableId: string) => void
  onColumnClear: () => void
}

/**
 * Renders the options for selecting the lookup table for a lookup column
 */
export function LookupTableOptions({
  tableOptions,
  highlightedOption,
  setSearchQuery,
  searchQuery,
  setDropdownEntity,
  selectedTableId,
  onTableSelect,
  onColumnClear,
}: LookupTableOptionsProps) {
  const { t } = useTypedTranslation()
  const highlightedRef = useRef<HTMLDivElement>(null)

  // Scroll to highlighted option
  useEffect(() => {
    if (highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }
  }, [highlightedOption])

  const handleTableSelection = (tableId: string) => {
    if (selectedTableId !== tableId) {
      onTableSelect(tableId)
      onColumnClear()
    }
    setSearchQuery('')
    setDropdownEntity(LookupTableAndFieldDropdownEntity.FIELD)
  }

  const isSelected = (tableId: string) => {
    return selectedTableId === tableId
  }

  const isHighlighted = (tableId: string) => {
    return highlightedOption?.id === tableId
  }

  return (
    <div className="flex h-full flex-1 flex-col overflow-hidden">
      <div
        className={`flex items-center justify-between gap-1 border-b border-gray-4 bg-gray-1 ${selectedTableId ? 'py-1 ps-2' : 'p-2'}`}
      >
        <Typography className="text-gray-12" variant="body3">
          {t('table_column.select_a_table')}
        </Typography>
        {selectedTableId && (
          <IconButton
            onClick={() => {
              setDropdownEntity(LookupTableAndFieldDropdownEntity.FIELD)
              setSearchQuery('')
            }}
            size="small"
            variant="true-ghost"
          >
            <RxArrowRight size={12} />
          </IconButton>
        )}
      </div>
      <div className="flex flex-1 flex-col overflow-auto">
        {tableOptions.map((option) => {
          return (
            <div
              key={option.id}
              ref={isHighlighted(option.id) ? highlightedRef : null}
              className={cn(
                `flex cursor-pointer items-center gap-2 p-2 hover:bg-gray-1`,
                isHighlighted(option.id) && 'bg-gray-1',
                isSelected(option.id) && 'bg-blue-2',
              )}
              onClick={() => handleTableSelection(option.id)}
            >
              <WorkspaceIcon
                icon={option.icon ? <DossIcon name={option.icon.name} size={12} /> : undefined}
                workspaceType={BreadcrumbObjectType.TABLE}
              />
              <div className="flex flex-col truncate">
                <Typography className="truncate text-nowrap" variant="body3">
                  <Highlighter
                    autoEscape
                    highlightStyle={{
                      backgroundColor: 'transparent',
                      fontWeight: 600,
                    }}
                    searchWords={[searchQuery]}
                    textToHighlight={option.name}
                  />
                </Typography>
                {option.parentTable && (
                  <Typography className="truncate text-nowrap text-gray-9" variant="body3">
                    {t('lookup.child_of_parent', { parentName: option.parentTable.name })}
                  </Typography>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
